import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Typography, Alert, Spin, Select } from 'antd';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { getProjects, getProjectDashboard } from '../services/api';

const { Title, Text } = Typography;

interface ProjectDashboard {
  project: string;
  ncr_summary: {
    critical: number;
    major: number;
    minor: number;
    total_open: number;
  };
  dpr_today: {
    submitted: boolean;
    actual_progress_pct: string | null;
    manpower_count: number | null;
  };
  pending_mom_actions: number;
  overdue_items: number;
  unacknowledged_instructions: number;
  as_of: string;
}

interface Project {
  id: string;
  code: string;
  name: string;
}

const ProjectDashboardPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [data, setData] = useState<ProjectDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
        loadDashboard(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadDashboard = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getProjectDashboard(projectId);
      setData(response.data);
    } catch (err) {
      setError('Failed to load project dashboard.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadDashboard(value);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          Project Dashboard
        </Title>
        <Select
          value={selectedProject}
          onChange={handleProjectChange}
          style={{ width: 300 }}
          options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
        />
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      {data && (
        <>
          <Title level={5} style={{ color: '#1B2A4A', marginBottom: 16 }}>NCR Status</Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #ff4d4f' }}>
                <Statistic
                  title="Critical NCRs"
                  value={data.ncr_summary.critical}
                  prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f', fontSize: 36 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #faad14' }}>
                <Statistic
                  title="Major NCRs"
                  value={data.ncr_summary.major}
                  prefix={<WarningOutlined style={{ color: '#faad14' }} />}
                  valueStyle={{ color: '#faad14', fontSize: 36 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #52c41a' }}>
                <Statistic
                  title="Minor NCRs"
                  value={data.ncr_summary.minor}
                  prefix={<WarningOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a', fontSize: 36 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #1890ff' }}>
                <Statistic
                  title="Total Open NCRs"
                  value={data.ncr_summary.total_open}
                  valueStyle={{ color: '#1890ff', fontSize: 36 }}
                />
              </Card>
            </Col>
          </Row>

          <Title level={5} style={{ color: '#1B2A4A', marginBottom: 16 }}>Today's Status</Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: data.dpr_today.submitted ? '4px solid #52c41a' : '4px solid #ff4d4f' }}>
                <Statistic
                  title="DPR Today"
                  value={data.dpr_today.submitted ? 'Submitted' : 'Not Submitted'}
                  prefix={data.dpr_today.submitted
                    ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    : <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: data.dpr_today.submitted ? '#52c41a' : '#ff4d4f', fontSize: 24 }}
                />
                {data.dpr_today.submitted && (
                  <Text type="secondary">
                    Progress: {data.dpr_today.actual_progress_pct}% | Manpower: {data.dpr_today.manpower_count}
                  </Text>
                )}
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #faad14' }}>
                <Statistic
                  title="Pending MOM Actions"
                  value={data.pending_mom_actions}
                  prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
                  valueStyle={{ color: '#faad14', fontSize: 36 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #ff4d4f' }}>
                <Statistic
                  title="Overdue Items"
                  value={data.overdue_items}
                  prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f', fontSize: 36 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #722ed1' }}>
                <Statistic
                  title="Unacknowledged Instructions"
                  value={data.unacknowledged_instructions}
                  valueStyle={{ color: '#722ed1', fontSize: 36 }}
                />
              </Card>
            </Col>
          </Row>

          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last updated: {new Date(data.as_of).toLocaleString()}
            </Text>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectDashboardPage;